import { useState } from "react";
import { listed } from "../../shared/words";
import type { CostDraft } from "./data";
import type { ItemDraft } from "./itemDrafts";
import { changedDetails, type DetailsDraft, savedDetails } from "./ItemDetailsTab";
import type { MaterialRow, PackagePart, PriceListData } from "./meta";
import { type PriceDraft, priceDraftUnsaved } from "./SupplierPricesTab";
import { sameTags } from "./TagPicker";
import { useLeftUnsaved } from "./windows";

/*
 * An item window's drafts (ItemWindow.tsx), each kept while other tabs are open: its pricing, cost, products, details, tags and the
 * supplier price being added. Which are unsaved, what leaving would lose, and which tab to show when asking.
 */

/** What's not saved yet, when leaving it asks: its pricing, products, cost, details, tags, the supplier price being added. */
function unsavedText(pricing: boolean, details: boolean, supplierPrice: boolean, cost = false, tags = false, parts = false) {
    // Each, and whether it reads as many ("its tags aren't").
    const things = (
        [
            [pricing, "its pricing", false],
            [parts, "its products", true],
            [cost, "its cost", false],
            [details, "its details", true],
            [tags, "its tags", true],
            [supplierPrice, "the supplier price being added", false],
        ] as const
    ).filter(([on]) => on);
    if (things.length === 1 && pricing) return "Your changes to its pricing aren't saved yet.";
    const names = listed(things.map(([, name]) => name));
    const many = things.length > 1 || things[0]?.[2] === true;
    return `${names.charAt(0).toUpperCase()}${names.slice(1)} ${many ? "aren't" : "isn't"} saved yet.`;
}

/** An item window's drafts, for the item `uuid` (started afresh when another is opened: `onOtherItem` too), on its tab `tab`. */
export function useItemDrafts(uuid: string, tab: string, data: PriceListData, item: MaterialRow | undefined, product: boolean, onOtherItem: () => void) {
    const [draft, setDraft] = useState<ItemDraft | null>(null);
    // Its name, item number and barcode being changed (its Details tab: saved with its tags, together): kept while other tabs are open.
    const [detailsDraft, setDetailsDraft] = useState<DetailsDraft | null>(null);
    // Its tags being edited (its Details tab): kept while other tabs are open; null, not being edited.
    const [tagsDraft, setTagsDraft] = useState<string[] | null>(null);
    // A supplier price being added (its Suppliers tab's form): kept while other tabs are open.
    const [priceDraft, setPriceDraft] = useState<PriceDraft | null>(null);
    // Its cost being changed (its Costing tab's Cost card): kept while other tabs are open, like its pricing.
    const [costDraft, setCostDraft] = useState<CostDraft | null>(null);
    // What it's built from being changed (its Contents tab): saved together, its cost and price with them.
    const [partsDraft, setPartsDraft] = useState<PackagePart[] | null>(null);
    // Another item opened here: its drafts started afresh in the same render (an effect would show the last item's for one render).
    const [draftsOf, setDraftsOf] = useState(uuid);
    if (draftsOf !== uuid) {
        setDraftsOf(uuid);
        setDraft(null);
        setCostDraft(null);
        setPartsDraft(null);
        setDetailsDraft(null);
        setTagsDraft(null);
        setPriceDraft(null);
        onOtherItem();
    }
    const edited = draft !== null;
    // Its details changed and not saved (they save only with their save bar): kept while other tabs are open. Only what still
    // differs from it as saved (someone else may have saved the same since).
    const savedNow = item ? savedDetails(data, item, product) : null;
    const details = savedNow && detailsDraft ? changedDetails({ ...savedNow, ...detailsDraft }, savedNow) : null;
    const detailsUnsaved = details !== null;
    const priceUnsaved = priceDraftUnsaved(priceDraft);
    const costUnsaved = costDraft !== null;
    const partsUnsaved = partsDraft !== null;
    // Open with no change isn't unsaved (its own tags: those from its products and group aren't in the draft).
    const tagsUnsaved = tagsDraft !== null && item !== undefined && !sameTags(tagsDraft, data.itemTags[item.uuid] ?? []);
    const unsaved = edited || detailsUnsaved || priceUnsaved || costUnsaved || tagsUnsaved || partsUnsaved;
    // Those to show in Needs attention: once moved off their tab.
    const left = useLeftUnsaved(tab, {
        pricing: { unsaved: edited, tab: "pricing" },
        details: { unsaved: detailsUnsaved, tab: "details" },
        price: { unsaved: priceUnsaved, tab: "suppliers" },
        cost: { unsaved: costUnsaved, tab: "suppliers" },
        tags: { unsaved: tagsUnsaved, tab: "details" },
        parts: { unsaved: partsUnsaved, tab: "contents" },
    });
    const unsavedNow = unsavedText(edited, detailsUnsaved, priceUnsaved, costUnsaved, tagsUnsaved, partsUnsaved);
    // Asking about them, the tab with them (and their save buttons) in view: the pricing, else the item number, else the cost, else the supplier price.
    const askTab = () => {
        if (edited) return "pricing";
        if (detailsUnsaved || tagsUnsaved) return "details";
        if (partsUnsaved) return "contents";
        return "suppliers";
    };
    // A tab's mark: changes on it not saved yet.
    const tabUnsaved = (id: string) =>
        (id === "pricing" && edited) || (id === "details" && (detailsUnsaved || tagsUnsaved)) || (id === "contents" && partsUnsaved) || (id === "suppliers" && (costUnsaved || priceUnsaved));
    return {
        draft,
        setDraft,
        /** Its details' draft: only what still differs from it as saved (null: nothing). */
        details,
        setDetailsDraft,
        tagsDraft,
        setTagsDraft,
        priceDraft,
        setPriceDraft,
        costDraft,
        setCostDraft,
        partsDraft,
        setPartsDraft,
        edited,
        unsaved,
        left,
        unsavedNow,
        askTab,
        tabUnsaved,
    };
}
